import { useCallback, useEffect, useRef, useState } from "react";
import { loadPanelLayout, savePanelPosition, type PanelPosition } from "../editor/persist.js";

/**
 * Cuánto de un panel tiene que seguir dentro de la pantalla.
 *
 * No se exige que quepa entero: arrastrar uno medio fuera para despejar el mapa es un
 * gesto legítimo. Lo que no puede pasar es que salga del todo, porque entonces no hay
 * forma de volver a agarrarlo sin borrar el almacenamiento a mano.
 */
const KEEP_VISIBLE = 120;

/** La barra de título siempre alcanzable: es lo único por lo que se arrastra. */
const BAR_HEIGHT = 44;

/**
 * Contador de apilamiento, a nivel de módulo.
 *
 * Compartido a propósito: "el último que tocaste va delante" sólo funciona si los
 * paneles se comparan ENTRE SÍ, y un contador por panel les daría a todos el mismo
 * número. Por eso vive acá y no en cada componente — desde que la ficha también se
 * arrastra, son dos familias de panel compitiendo por el mismo frente.
 */
let frontmost = 10;

interface Options {
  /** Clave de persistencia. Estable, o el panel olvida dónde lo dejaste. */
  id: string;
  width: number;
  /**
   * Dónde nace si no hay nada guardado. Recibe el viewport porque los paneles de la
   * derecha y de abajo se colocan respecto al borde, no respecto al origen.
   */
  defaultPosition: (viewport: { width: number; height: number }) => PanelPosition;
}

/**
 * Arrastrar un panel por su barra, sin decidir qué aspecto tiene.
 *
 * Se extrajo de `DraggablePanel` cuando la ficha del jugador también tuvo que poder
 * moverse: el comportamiento —recortar contra la pantalla, recordar dónde lo dejaste,
 * traer al frente lo último que tocaste— es el mismo, y el cromo no. Duplicarlo habría
 * dejado dos contadores de apilamiento que no se ven entre sí, que es justo el bug de
 * "el panel que traigo al frente se queda detrás del otro".
 */
export function useDraggable<T extends HTMLElement>({ id, width, defaultPosition }: Options) {
  const ref = useRef<T>(null);
  const [z, setZ] = useState(() => ++frontmost);

  /**
   * El agarre: dónde caía el puntero DENTRO del panel al empezar.
   *
   * Sin esto el panel salta para poner su esquina bajo el cursor en el primer
   * movimiento. Guardarlo hace que el punto que agarraste sea el que sigue al ratón.
   */
  const grab = useRef<{ dx: number; dy: number } | null>(null);

  const clamp = useCallback(
    (point: PanelPosition): PanelPosition => {
      const w = ref.current?.offsetWidth ?? width;

      return {
        x: Math.max(KEEP_VISIBLE - w, Math.min(point.x, window.innerWidth - KEEP_VISIBLE)),
        y: Math.max(0, Math.min(point.y, window.innerHeight - BAR_HEIGHT)),
      };
    },
    [width],
  );

  const [position, setPosition] = useState<PanelPosition>(() => {
    const saved = loadPanelLayout()[id];
    if (saved) return saved;

    return defaultPosition({ width: window.innerWidth, height: window.innerHeight });
  });

  /**
   * La última posición, fuera del estado.
   *
   * Persistir desde dentro de un actualizador de `setPosition` sería un efecto en una
   * función que React puede invocar dos veces en StrictMode. Una ref da el valor
   * fresco al soltar sin ensuciar el actualizador.
   */
  const latest = useRef(position);

  const moveTo = useCallback(
    (point: PanelPosition) => {
      const next = clamp(point);
      latest.current = next;
      setPosition(next);
    },
    [clamp],
  );

  // Encoger la ventana puede dejar un panel fuera; se recoloca en vez de perderse.
  useEffect(() => {
    const onResize = () => moveTo(latest.current);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [moveTo]);

  const bringToFront = useCallback(() => setZ(++frontmost), []);

  const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    // Sólo el botón principal, y nunca desde un control que viva en la barra.
    if (event.button !== 0) return;
    if ((event.target as HTMLElement).closest("button, input, select")) return;

    const box = ref.current?.getBoundingClientRect();
    grab.current = {
      dx: event.clientX - (box?.left ?? position.x),
      dy: event.clientY - (box?.top ?? position.y),
    };

    setZ(++frontmost);
    // La captura es lo que mantiene vivo el arrastre si el puntero se sale del panel
    // — que es lo normal al mover rápido.
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    const held = grab.current;
    if (!held) return;

    moveTo({ x: event.clientX - held.dx, y: event.clientY - held.dy });
  };

  const endDrag = (event: React.PointerEvent<HTMLElement>) => {
    if (!grab.current) return;
    grab.current = null;

    event.currentTarget.releasePointerCapture(event.pointerId);
    // Se persiste al soltar, no en cada fotograma: escribir en `localStorage` a 60Hz
    // es trabajo síncrono en el mismo hilo que está dibujando el arrastre.
    savePanelPosition(id, latest.current);
  };

  return {
    ref,
    /** Va en el elemento del panel. `fixed` lo pone el CSS; esto sólo lo coloca. */
    style: { left: `${position.x}px`, top: `${position.y}px`, zIndex: z } as const,
    /** En el panel entero: tocarlo lo trae al frente aunque no se arrastre. */
    bringToFront,
    /** En la barra por la que se arrastra, y sólo en ella. */
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    },
  };
}
